import React, { useState, useRef } from 'react';
import { Upload, FileText, Download, Check, AlertCircle, Users } from 'lucide-react';
import { uploadGuestList, parseExcelFile, parseCSVFile, generateSampleTemplate } from '../lib/tables';
import type { GuestListUpload } from '../types/tables';

interface Props {
  registryId: string;
  onUploadSuccess: () => void;
}

export default function GuestListUploader({ registryId, onUploadSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [parsedData, setParsedData] = useState<GuestListUpload[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError('');
    setSuccess('');
    setParsedData([]);
    setShowPreview(false);

    try {
      let parsedGuests: GuestListUpload[] = [];
      
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        parsedGuests = await parseExcelFile(file);
      } else if (file.name.endsWith('.csv')) {
        parsedGuests = await parseCSVFile(file);
      } else {
        throw new Error('Unsupported file format. Please upload a CSV or Excel file.');
      }

      setParsedData(parsedGuests);
      setShowPreview(true);
    } catch (err) {
      console.error('Error parsing file:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!parsedData.length) {
      setError('No data to upload');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await uploadGuestList(registryId, parsedData);
      
      if (result.success) {
        setSuccess(result.message);
        setUploadCompleted(true);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error uploading guest list:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload guest list');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    setParsedData([]);
    setShowPreview(false);
    setUploadCompleted(false);
    setSuccess('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onUploadSuccess();
  };

  const handleDownloadTemplate = () => {
    generateSampleTemplate();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-pastel-purple-600" />
          <h3 className="text-lg font-medium text-gray-800 font-poppins">Guest List Upload</h3>
        </div>
        <button
          type="button"
          onClick={handleDownloadTemplate}
          className="flex items-center space-x-1 px-3 py-1 text-sm bg-white/60 rounded-md hover:bg-white/80 text-gray-600 hover:text-gray-800"
        >
          <Download className="w-4 h-4" />
          <span>Template</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-md flex items-center space-x-2">
          <Check className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      <div className="card-pastel p-4 rounded-lg">
        <div className="flex items-center space-x-2 mb-4">
          <FileText className="w-5 h-5 text-gray-600" />
          <span className="text-gray-800 font-medium">Upload Guest List</span>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Upload a CSV or Excel file with guest information. The file should include columns for Guest Name, Contact Info, Dietary Restrictions, Plus One Status, and Plus One Name.
        </p>
        
        <div className="flex flex-col space-y-4">
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/40 rounded-lg cursor-pointer bg-white/60 hover:bg-white/80 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <p className="mb-2 text-sm text-gray-500">
                <span className="font-semibold">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-gray-500">CSV or Excel files only</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              disabled={loading}
            />
          </label>
          
          {showPreview && parsedData.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-gray-800 font-medium">Preview ({parsedData.length} guests)</h4>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={loading || uploadCompleted}
                  className="px-4 py-2 btn-pastel text-white rounded-md hover:opacity-90 disabled:opacity-50"
                >
                  {loading ? 'Uploading...' : uploadCompleted ? 'Upload Complete' : 'Confirm Upload'}
                </button>
              </div>
              
              <div className="max-h-60 overflow-y-auto bg-white/60 rounded-lg">
                <table className="w-full text-left">
                  <thead className="bg-white/80 text-gray-600 text-sm">
                    <tr>
                      <th className="px-4 py-2">Guest Name</th>
                      <th className="px-4 py-2">Contact Info</th>
                      <th className="px-4 py-2">Dietary Restrictions</th>
                      <th className="px-4 py-2">Plus One</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/40">
                    {parsedData.slice(0, 100).map((guest, index) => (
                      <tr key={index} className="text-gray-600">
                        <td className="px-4 py-2">{guest.guest_name}</td>
                        <td className="px-4 py-2">{guest.contact_info || '-'}</td>
                        <td className="px-4 py-2">{guest.dietary_restrictions || '-'}</td>
                        <td className="px-4 py-2">
                          {guest.plus_one_status ? (guest.plus_one_name || 'Yes') : 'No'}
                        </td>
                      </tr>
                    ))}
                    {parsedData.length > 100 && (
                      <tr className="text-gray-500">
                        <td colSpan={4} className="px-4 py-2 text-center">
                          ...and {parsedData.length - 100} more guests
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {uploadCompleted ? (
                <div className="space-y-3">
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-green-700 text-sm">
                      ✓ Guest list uploaded successfully! Guests can now RSVP to your event.
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={handleDone}
                      className="px-6 py-2 btn-pastel text-white rounded-md"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-pastel-blue-50 p-3 rounded-lg">
                  <p className="text-pastel-blue-700 text-sm">
                    ✓ Guest list parsed successfully. Click "Confirm Upload" to save.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
